import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useBranding } from '../TournamentBrandingContext';
import { substituteTokens } from '../utils/tournamentTokens';
import {
  Box, Container, Typography, Card, CardContent, Button,
  CircularProgress, Chip, Divider, List, ListItem, ListItemText,
  Breadcrumbs, Link, Grid, Alert,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { logError } from '../utils/errorLogger';

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[+m - 1]} ${+d}, ${y}`;
}

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

const GROUP_ORDER = ['Adult', 'Junior', 'Camps & Clinics'];
const GROUP_COLORS = { Adult: 'primary', Junior: 'success', 'Camps & Clinics': 'info' };

const SANCTIONING_LOGOS = {
  'AVP':   { bg: '#002B5C', color: '#FFD700', font: 'Arial Black, sans-serif', logo: '/avp.png' },
  'USAV':  { bg: '#002868', color: '#BF0A30', font: 'Arial Black, sans-serif', logo: '/usav.png' },
  'p1440': { bg: '#000000', color: '#C8A951', font: 'Georgia, serif',          logo: '/p1440.png' },
  'BVNE':  { bg: '#E8412A', color: 'white',   font: 'Arial Black, sans-serif', logo: '/bvne.png' },
};

export default function TournamentDetail() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const b = useBranding();
  const heroBg = `linear-gradient(135deg, ${b.primary} 0%, ${b.secondary} 100%)`;

  const [tournament, setTournament] = useState(null);
  const [divisionData, setDivisionData] = useState({});
  const [divList, setDivList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [waves, setWaves] = useState([]);
  const [partialReg, setPartialReg] = useState(null);
  const [hasResults, setHasResults] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const tSnap = await getDoc(doc(db, 'tournaments', tid));
        if (!tSnap.exists()) { setLoading(false); return; }
        const t = { id: tSnap.id, ...tSnap.data() };
        setTournament(t);
        const divisions = Array.isArray(t.divisions) ? t.divisions : JSON.parse(t.divisions || '[]');
        const divSnap = await getDocs(collection(db, 'divisions'));
        const fetchedDivList = divSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setDivList(fetchedDivList);
        const regSnap = await getDocs(query(collection(db, 'registrations'), where('tournament_id', '==', tid)));
        const regs = regSnap.docs.map(d => d.data()).filter(r => r.status !== 'cancelled');
        const prices = typeof t.prices === 'string' ? JSON.parse(t.prices) : (t.prices || {});
        const freeIfReg = t.free_if_registered || {};
        const caps   = typeof t.caps   === 'string' ? JSON.parse(t.caps)   : (t.caps   || {});
        const data = {};
        for (const div of divisions) {
          const divRegs  = regs.filter(r => r.division === div);
          const cap      = caps[div] ? Number(caps[div]) : null;
          const divRecord = fetchedDivList.find(d => d.name === div);
          const price    = prices[div] != null ? Number(prices[div]) : Number(divRecord?.price || 0);
          const mainRegs = divRegs.filter(r => r.waitlist_position == null || r.waitlist_position === 0);
          const waitRegs = divRegs.filter(r => r.waitlist_position > 0).sort((a, b) => a.waitlist_position - b.waitlist_position);
          function teamLabel(r) {
            const p1 = `${r.first_name} ${r.last_name}`;
            const p2 = r.partner_name || (r.partner_first_name ? `${r.partner_first_name} ${r.partner_last_name || ''}`.trim() : null);
            return p2 ? `${p1} & ${p2}` : p1;
          }
          data[div] = { main: mainRegs.map(teamLabel), waitlist: waitRegs.map(teamLabel), count: mainRegs.length, cap, price, freeIfReg: freeIfReg[div] || false };
        }
        setDivisionData(data);
        setWaves(t.waves || []);
        // Check whether tournament results have been imported
        try {
          const resSnap = await getDoc(doc(db, 'tournamentResults', tid));
          setHasResults(resSnap.exists());
        } catch {
          setHasResults(false);
        }
      } catch (err) {
        // Ignore offline errors — non-critical for public tournament page
        if (!err.message?.includes('offline') && !err.code?.includes('unavailable')) {
          console.error(err);
          logError('TournamentDetail', err.message || String(err), err.stack || '');
        }
      } finally { setLoading(false); }
    }
    load();
  }, [tid]);

  // Check if current user has an unpaid or partial registration for this tournament
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user?.email) return;
      try {
        const snap = await getDocs(query(
          collection(db, 'registrations'),
          where('tournament_id', '==', tid),
          where('email', '==', user.email.toLowerCase()),
          where('status', '==', 'active')
        ));
        const unpaid = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .find(r => r.payment_status === 'half_paid' || r.payment_status === 'pending');
        if (unpaid) setPartialReg(unpaid);
      } catch {}
    });
    return unsub;
  }, [tid]);

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!tournament) return (
    <Container sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant="h5" color="text.secondary" gutterBottom>Tournament not found.</Typography>
      <Button variant="contained" onClick={() => navigate('/tournaments')}>Back to Tournaments</Button>
    </Container>
  );

  const divisions = Array.isArray(tournament.divisions) ? tournament.divisions : JSON.parse(tournament.divisions || '[]');
  const grouped = {};
  divisions.forEach(div => {
    const rec = divList.find(d => d.name === div);
    const group = rec?.group_name || 'Adult';
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(div);
  });
  const sortedGroups = [...GROUP_ORDER.filter(g => grouped[g]), ...Object.keys(grouped).filter(g => !GROUP_ORDER.includes(g))];

  return (
    <Box>
      <Box sx={{ background: heroBg, color: b.banner_color, py: 5 }}>
        <Container maxWidth="md">
          <Breadcrumbs sx={{ mb: 1, '& *': { color: `${b.banner_color}99` } }}>
            <Link underline="hover" sx={{ cursor: 'pointer', color: `${b.banner_color}99 !important` }}
                  onClick={() => navigate('/tournaments')}>Tournaments</Link>
            <Typography sx={{ color: b.banner_color }}>{tournament.name}</Typography>
          </Breadcrumbs>
          <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: b.banner_color }}>{tournament.name}</Typography>
          {hasResults && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<EmojiEventsIcon />}
                onClick={() => navigate(`/tournament/${tid}/results`)}
                sx={{
                  backgroundColor: '#C9A84C',
                  color: '#0D1E35',
                  fontWeight: 700,
                  letterSpacing: 1,
                  '&:hover': { backgroundColor: '#B8973F' },
                }}
              >
                View Tournament Results
              </Button>
            </Box>
          )}
          {tournament.sanctioning_body && tournament.sanctioning_body !== 'None' && (() => {
            const logo = SANCTIONING_LOGOS[tournament.sanctioning_body];
            return (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', mb: 1,
                backgroundColor: logo?.bg || 'rgba(255,255,255,0.2)',
                borderRadius: 1, px: 1.5, py: 0.5, gap: 0.75 }}>
                {logo?.logo ? (
                  <img src={logo.logo} alt={tournament.sanctioning_body}
                    style={{ height: 18, objectFit: 'contain', display: 'block' }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <Typography variant="caption" sx={{
                    color: logo?.color || 'white', fontWeight: 900, letterSpacing: 2,
                    fontFamily: logo?.font || 'Arial Black, sans-serif',
                  }}>
                    {tournament.sanctioning_body}
                  </Typography>
                )}
              </Box>
            );
          })()}
          {tournament.close_date && (() => {
            const closeDt = new Date(`${tournament.close_date}T${tournament.close_time || '23:59'}`);
            const isClosed = new Date() > closeDt;
            if (isClosed) return (
              <Chip label="Registration Closed" size="small"
                sx={{ mb: 1, ml: 1, backgroundColor: '#E74C3C', color: 'white', fontWeight: 700 }} />
            );
            return (
              <Chip label={`Registration closes ${fmtDate(tournament.close_date)}${tournament.close_time ? ' at ' + fmtTime(tournament.close_time) : ''}`}
                size="small" sx={{ mb: 1, ml: 1, backgroundColor: 'rgba(255,255,255,0.2)', color: b.banner_color }} />
            );
          })()}
          {tournament.info_html ? (
            <Box dangerouslySetInnerHTML={{ __html: substituteTokens(tournament.info_html, tournament) }} sx={{ maxWidth: 680, color: b.banner_color }} />
          ) : (
            <Typography variant="h6" sx={{ opacity: 0.85, color: b.banner_color }}>
              <CalendarTodayIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />{fmtDate(tournament.date)}
              {tournament.location && <> &nbsp;<LocationOnIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />{tournament.location}</>}
            </Typography>
          )}
        </Container>
      </Box>

      {/* Partial/pending payment banner */}
      {partialReg && (
        <Box sx={{ background: '#fff8e1', borderBottom: '1px solid #ffe082' }}>
          <Container maxWidth="lg" sx={{ py: 2 }}>
            <Alert
              severity="warning"
              action={
                <Button size="small" variant="contained"
                  href={`/tournament-pay?reg_id=${partialReg.id}`}
                  sx={{ backgroundColor: '#1a2940', color: 'white', fontWeight: 700,
                    whiteSpace: 'nowrap', '&:hover': { backgroundColor: '#2c4a7c' } }}>
                  Pay Now →
                </Button>
              }
              sx={{ alignItems: 'center' }}>
              {partialReg.payment_status === 'half_paid'
                ? <>You have a 50% deposit on file for the <strong>{partialReg.division}</strong> division (${(partialReg.amount_paid / 100).toFixed(2)} paid). Your balance is due before the tournament.</>
                : <>You registered for the <strong>{partialReg.division}</strong> division with payment due at the tournament. Pay online now to secure your spot.</>}
            </Alert>
          </Container>
        </Box>
      )}

      <Container maxWidth="lg" sx={{ py: 5 }}>
        {sortedGroups.map(groupName => (
          <Box key={groupName} sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Chip label={groupName} color={GROUP_COLORS[groupName] || 'default'} />
              <Divider sx={{ flexGrow: 1 }} />
            </Box>
            <Grid container spacing={3}>
              {grouped[groupName].map(div => {
                const d = divisionData[div] || { main: [], waitlist: [], count: 0, cap: null, price: 0 };
                const spotsLeft = d.cap != null ? d.cap - d.count : 99;
                const isFull = d.cap != null && spotsLeft <= 0;
                return (
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={div}>
                    <Card elevation={2} sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="h6" fontWeight="bold">{div}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <Chip label={d.price > 0 ? `$${d.price.toFixed(2)} / ${groupName === 'Camps & Clinics' ? 'person' : 'team'}` : 'Free'} color="success" size="small" />
                            {d.freeIfReg && <Chip label="Free w/ reg" color="info" size="small" />}
                          </Box>
                        </Box>
                        {(() => { const w = waves.find(w => (w.divisions||[]).includes(div)); return w ? (
                          <Box sx={{ mb: 1 }}>
                            <Chip size="small" color="primary" variant="outlined"
                                  label={`${w.name} · Day ${w.day} · ${fmtTime(w.start_time)}–${fmtTime(w.end_time)}`}
                                  sx={{ fontSize: 11 }} />
                          </Box>
                        ) : null; })()}
                        {d.cap != null ? (
                          spotsLeft > 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {d.count} / {d.cap} teams —{' '}
                              <Typography component="span" variant="body2" color="success.main" fontWeight="bold">
                                {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} left
                              </Typography>
                            </Typography>
                          ) : <Chip label="Main Draw Full – Waitlist Open" color="warning" size="small" sx={{ mb: 1 }} />
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {d.count} team{d.count !== 1 ? 's' : ''} registered
                          </Typography>
                        )}
                        {d.main.length > 0 ? (
                          <>
                            {d.waitlist.length > 0 && <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>Main Draw</Typography>}
                            <List dense disablePadding>
                              {d.main.map((team, i) => (
                                <ListItem key={i} disablePadding sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                                  <PeopleIcon sx={{ fontSize: 14, mr: 1, color: 'primary.main' }} />
                                  <ListItemText primary={team} primaryTypographyProps={{ variant: 'body2' }} />
                                </ListItem>
                              ))}
                            </List>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">No teams yet — be the first!</Typography>
                        )}
                        {d.waitlist.length > 0 && (
                          <>
                            <Typography variant="caption" color="warning.main" sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mt: 2 }}>
                              <AccessTimeIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />Waitlist
                            </Typography>
                            <List dense disablePadding>
                              {d.waitlist.map((team, i) => (
                                <ListItem key={i} disablePadding sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="body2" color="warning.main" sx={{ mr: 1 }}>{i + 1}.</Typography>
                                  <ListItemText primary={team} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}
                      </CardContent>
                      <Box sx={{ p: 2, pt: 0 }}>
                        {(() => {
                          const now = new Date();
                          const closeDt = tournament.close_date
                            ? new Date(`${tournament.close_date}T${tournament.close_time || '23:59'}`)
                            : null;
                          const isClosed = closeDt && now > closeDt;
                          if (isClosed) return (
                            <Chip label="Registration Closed" color="error" sx={{ width: '100%', borderRadius: 1, height: 36, fontWeight: 700 }} />
                          );
                          return (
                            <Button fullWidth variant="contained"
                              sx={{ bgcolor: isFull ? undefined : b.button_bg, color: isFull ? undefined : b.button_text, '&:hover': { bgcolor: isFull ? undefined : b.primary } }}
                              color={isFull ? 'warning' : undefined}
                              endIcon={isFull ? <AccessTimeIcon /> : <ArrowForwardIcon />}
                              onClick={() => navigate(`/tournament/${tid}/${encodeURIComponent(div)}`)}>
                              {isFull ? 'Join Waitlist' : 'Register'}
                            </Button>
                          );
                        })()}
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/tournaments')}>Back to Tournaments</Button>
        </Box>
      </Container>

      {b.footer && (
        <Box sx={{ bgcolor: b.primary, color: 'rgba(255,255,255,0.6)', py: 2, textAlign: 'center' }}>
          <Typography variant="caption">{b.footer}</Typography>
        </Box>
      )}
    </Box>
  );
}

// NOTE: Wave info added to division data loading and card display
// In loadData(), after setting divisionData, also set waves from tournament
// Division cards show wave chip if assigned
